/*
 * A copy of the dispatch console's module of the same name, kept byte-for-byte except for the
 * storage keys and the prose that named the other console.
 *
 * Nothing in it is design content: it is the OpenID Connect authorization-code flow with PKCE, the
 * token refresh, the expiry and the claim reading, all of which every console needs identically.
 * It is a copy rather than a shared package because this repository builds every package in
 * `packages/` through three explicit steps in the root `package.json`, and a sixth package that
 * `npm run check` could not build before its consumers would be worse than this duplication.
 * `docs/adr/0021-two-consoles-and-no-shared-shell.md` records the decision, what it costs, and the
 * precondition for undoing it.
 */
/**
 * Who is using the console, and which tenant they are looking at.
 *
 * Both are read from the access token, never chosen by the application. The
 * scopes decide what is drawn and the tenant decides what is asked for, and a
 * console that let either be set locally would be deciding its own
 * authorization — which `apps/README.md` forbids, and which every service would
 * refuse anyway.
 *
 * The tenant deserves its own paragraph. A platform merchant can cross the
 * tenant boundary, the design commission requires that they always know which
 * side they are on, and the consequence of getting it wrong is not a wrong
 * screen — it is a merchant cancelling a real trip belonging to a company
 * they have never heard of. So the tenant is never implicit: it is part of the
 * session, it is rendered in the chrome, and switching it is an act rather than
 * a side effect of navigation.
 */

export interface Session {
  readonly subject: string;
  readonly displayName: string;
  readonly scopes: ReadonlySet<string>;
  readonly tenantId: string;
  readonly tenantName: string;
  /** True when this actor may act across tenants. Changes what the chrome says. */
  readonly crossTenant: boolean;
  /**
   * The merchant's own identifier, where the subject is one.
   *
   * Ten services compare `actor.subject` against a domain identifier — a customer, a driver, a rider,
   * a user — and each of those is a uuid, so the platform's convention is that a token's `sub` *is*
   * the identifier of the person it belongs to. The header's notification bell needs it, because
   * NTF-05 addresses an inbox as `/v1/tenants/{tenantId}/users/{userId}/…` and refuses unless the
   * subject equals that path.
   *
   * Undefined when the subject is not a uuid. That is not a fallback to guess around: an inbox
   * addressed with the wrong identifier is another merchant's inbox, and the header shows no count
   * rather than the wrong one.
   */
  readonly userId?: string;
  /**
   * The merchant this session acts for, from a `merchant_id` claim.
   *
   * **This is the first channel on the platform that holds more than a tenant**, and that is what
   * makes the claim necessary rather than convenient. Three of this portal's reads are addressed
   * `/v1/merchants/{merchantId}/…` — COM-06's board, COM-03's shelf, and MER-02's locations when
   * they are published — so a screen cannot form its own request without knowing which merchant it
   * is. The tenant is not enough: a tenant has many merchants, and answering one merchant's board
   * to another is the failure this field exists to prevent.
   *
   * Read from the token and never from a path, a query or a selector, for the same reason
   * `tenant_id` is: a value the browser supplies is a value the browser can change, and
   * `x-merchant-id` beside a token that does not carry one is the shape the runtime already
   * refuses for tenants. The services check it too — COM-06's listing is bounded by the verified
   * tenant and the path merchant together — so this is what makes the screen *able* to ask, not
   * what makes the answer safe.
   *
   * **Undefined when the claim is absent, and the boards that need it say so.** That is the same
   * decision ADR 0079 took for `driver_id`: it refused to derive the identifier from `sub`, because
   * a subject is who signed in and a merchant is what they act for, and the two are only equal by
   * accident. A board shown an empty list because the claim was missing would read as a merchant
   * with no orders.
   *
   * The realm has no `merchant_id` mapper yet, which is recorded in `docs/design/DESIGN_QUESTIONS.md`
   * rather than worked around: it is one protocol mapper and one user attribute, exactly as
   * ADR 0079 added for drivers, and it is a realm change rather than a code change.
   */
  readonly merchantId?: string;
  /**
   * The branch this session acts for, from a `location_id` claim.
   *
   * COM-03's rows are keyed `(tenant, merchant, location, sku)`, so a merchant with two branches
   * has two shelves and the stock board must know which. The design's own update form opens with
   * `Branch (select)`, which is the same reading from the other side.
   *
   * On the token for the same reason as `merchantId`: a branch the browser supplies is a branch the
   * browser can change, and a shelf read for the wrong branch is a count a merchant acts on.
   * Absent until the realm carries the mapper, and the stock board says so rather than choosing a
   * branch.
   */
  readonly locationId?: string;
  /**
   * What the avatar shows. Derived from the display name, which is presentation rather than identity.
   */
  readonly initials: string;
}

const UUID = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/**
 * Two letters for the avatar: the first of the first and last words, or the first two of one word.
 *
 * Upper-cased, because the design draws them upper-case, and taken from the display name rather than
 * from the subject — a merchant recognises their own initials and would not recognise a uuid's.
 */
function initialsOf(displayName: string): string {
  const words = displayName
    .split(/[\s._-]+/)
    .map((word) => word.trim())
    .filter((word) => /^[A-Za-z]/.test(word));
  if (words.length === 0) return displayName.slice(0, 2).toUpperCase();
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return `${words[0]![0]}${words.at(-1)![0]}`.toUpperCase();
}

interface AccessTokenClaims {
  readonly sub?: unknown;
  readonly name?: unknown;
  readonly scope?: unknown;
  readonly tenant_id?: unknown;
  readonly merchant_id?: unknown;
  readonly location_id?: unknown;
}

/**
 * Read a session out of an access token, without trusting it for anything but
 * display.
 *
 * The signature is verified by the services, not here — a browser cannot hold
 * the key, and a client that "validated" a token would be performing a check
 * whose failure it would also be responsible for reporting. What this does is
 * read the claims so the console can draw the right chrome and hide what the
 * merchant cannot use, and every request still carries the token to a service
 * that checks it properly.
 */
export function sessionFromClaims(
  claims: AccessTokenClaims,
  tenantName: string,
  options: { readonly crossTenant?: boolean } = {},
): Session | undefined {
  const subject = typeof claims.sub === "string" ? claims.sub : undefined;
  const tenantId = typeof claims.tenant_id === "string" ? claims.tenant_id : undefined;
  if (subject === undefined || tenantId === undefined) return undefined;

  const scope = claims.scope;
  const scopes = Array.isArray(scope)
    ? scope.filter((value): value is string => typeof value === "string")
    : typeof scope === "string"
      ? scope.split(" ").filter((value) => value.length > 0)
      : [];

  const displayName =
    typeof claims.name === "string" && claims.name.length > 0 ? claims.name : subject;

  return {
    subject,
    displayName,
    scopes: new Set(scopes),
    tenantId,
    tenantName,
    crossTenant: options.crossTenant ?? false,
    ...(UUID.test(subject) ? { userId: subject } : {}),
    /*
     * Only when it is a uuid, like `userId` above and for the same reason: every merchant-addressed
     * operation on this platform types `merchantId` as a uuid, so a claim carrying anything else
     * would produce a request the service refuses as malformed — and a 400 on every board reads as
     * a broken portal rather than as a realm that needs a mapper.
     */
    ...(typeof claims.merchant_id === "string" && UUID.test(claims.merchant_id)
      ? { merchantId: claims.merchant_id }
      : {}),
    ...(typeof claims.location_id === "string" && UUID.test(claims.location_id)
      ? { locationId: claims.location_id }
      : {}),
    initials: initialsOf(displayName),
  };
}
